import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Mail, MapPin } from 'lucide-react';

const WhatsAppIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
    <path d="M16 2C8.28 2 2 8.28 2 16c0 2.46.67 4.88 1.94 7.02L2 30l7.19-1.88A13.93 13.93 0 0 0 16 30c7.72 0 14-6.28 14-14S23.72 2 16 2zm0 25.5a11.44 11.44 0 0 1-5.84-1.6l-.42-.25-4.27 1.12 1.14-4.16-.27-.44A11.46 11.46 0 0 1 4.5 16C4.5 9.6 9.6 4.5 16 4.5S27.5 9.6 27.5 16 22.4 27.5 16 27.5zm6.29-8.57c-.34-.17-2.02-.99-2.33-1.1-.31-.11-.54-.17-.77.17-.23.34-.88 1.1-1.08 1.33-.2.23-.4.26-.74.09-.34-.17-1.44-.53-2.74-1.69-1.01-.9-1.69-2.01-1.89-2.35-.2-.34-.02-.52.15-.69.15-.15.34-.4.51-.6.17-.2.23-.34.34-.57.11-.23.06-.43-.03-.6-.09-.17-.77-1.86-1.06-2.55-.28-.67-.56-.58-.77-.59l-.66-.01c-.23 0-.6.09-.91.43-.31.34-1.2 1.17-1.2 2.86 0 1.68 1.22 3.31 1.39 3.54.17.23 2.4 3.67 5.82 5.14.81.35 1.45.56 1.94.72.82.26 1.56.22 2.15.13.66-.1 2.02-.82 2.31-1.62.28-.8.28-1.48.2-1.62-.09-.14-.31-.23-.65-.4z"/>
  </svg>
);

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================

const heroContainerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -28 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ContactPage = () => {
  const { pathname } = useLocation();
  const isContactRoute = pathname === '/contact';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://monkstack.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Contact',
        item: 'https://monkstack.com/contact',
      },
    ],
  };

  return (
    <div className="App">
      {isContactRoute && (
        <SEO
          title="Contact"
          description="Contact monkStack in Pune to discuss SaaS, computer vision, manufacturing ERP, or enterprise integrations. Email info@monkstack.com or call +91-8600-35-8700."
          url="https://monkstack.com/contact"
          structuredData={breadcrumbSchema}
        />
      )}
      {isContactRoute && <Navbar />}

      <section className="cp-page">

        {/* ── Hero ───────────────────────────────────────────────────────── */}
        <div className="cp-hero">
          <div className="container">
            <motion.div
              className="cp-hero-inner"
              initial="hidden"
              animate="visible"
              variants={heroContainerVariants}
            >
              <motion.div variants={fadeUp}>
                <div className="cp-eyebrow-pill">
                  <span className="cp-eyebrow-dot" aria-hidden="true" />
                  CONTACT US
                </div>
              </motion.div>

              <motion.h1 className="cp-headline" variants={fadeUp}>
                Let's build your product.
              </motion.h1>

              <motion.p className="cp-subtext" variants={fadeUp}>
                Share your idea, challenge, or project requirement. We'll guide you through
                building scalable, high-performing software.
              </motion.p>
            </motion.div>
          </div>
        </div>

        {/* ── Cards row ──────────────────────────────────────────────────── */}
        <div className="cp-cards-outer">
          <div className="container">
            <div className="cp-card-row">

              {/* Left — Contact Info card */}
              <motion.div
                className="cp-card"
                initial="hidden"
                animate="visible"
                variants={fadeLeft}
              >
                <h2 className="cp-card-heading">Contact Info</h2>
                <p className="cp-card-subtext">
                  Reach us directly at our email — we'll get back within 24 hours.
                </p>

                <div className="cp-contacts">
                  <div className="cp-contact-item cp-contact-item--email">
                    <span className="cp-contact-icon">
                      <Mail size={18} aria-hidden="true" />
                    </span>
                    <a href="mailto:info@monkstack.com" className="cp-contact-link cp-contact-link--email">
                      info@monkstack.com
                    </a>
                  </div>
                  <div className="cp-contact-item">
                    <span className="cp-contact-icon">
                      <WhatsAppIcon size={18} />
                    </span>
                    <a href="https://wa.me/918600358700" target="_blank" rel="noopener noreferrer" className="cp-contact-link">
                     +91 -8600 35 8700
                    </a>
                  </div>
                </div>

                <div className="cp-linkedin-row">
                  <a
                    href="https://in.linkedin.com/company/monkstack"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cp-contact-icon cp-linkedin-link"
                    aria-label="LinkedIn"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                      <rect x="2" y="9" width="4" height="12" />
                      <circle cx="4" cy="4" r="2" />
                    </svg>
                  </a>
                  <a
                    href="https://in.linkedin.com/company/monkstack"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cp-contact-link"
                  >
                    Monkstack Solutions LLP
                  </a>
                </div>

                <div className="cp-contact-item cp-location-item">
                  <span className="cp-contact-icon">
                    <MapPin size={18} aria-hidden="true" />
                  </span>
                  <a
                    href="https://www.google.com/maps/place/Monkstack+Solutions+LLP/@18.4555333,73.917852,17z/data=!4m14!1m7!3m6!1s0x3bc2eb7c34619abf:0x9d1608de772491db!2sMonkstack+Solutions+LLP!8m2!3d18.4555282!4d73.9204269!16s%2Fg%2F11mlxpg1_l!3m5!1s0x3bc2eb7c34619abf:0x9d1608de772491db!8m2!3d18.4555282!4d73.9204269!16s%2Fg%2F11mlxpg1_l?entry=ttu"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cp-contact-link"
                  >
                    Office No 311, Nyati Eternity,<br />Undri, Pune — 411060
                  </a>
                </div>
              </motion.div>


            </div>
          </div>
        </div>

      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
